import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useMemo } from "react";
import { generatePath, NavLink } from "react-router-dom";

import { TIME_TRACKINGS_PATH } from "~/constants/appRoutes.ts";
import type { StaffingClosingStatusesByMonth } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import { formatMonthLabel, getAggregatedStaffingClosingStatuses } from "~/modules/billing/utils/billingUtils.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

type Props = {
	closingStatuses: StaffingClosingStatusesByMonth[];
	columnCount?: number;
	users: Pick<User, "id" | "fullName">[];
};

export function StaffingClosingStatusList({ closingStatuses, columnCount = 1, users }: Props) {
	const showUnclosedMonths = closingStatuses.length > 1;

	const enrichedUsers = useMemo(() => {
		return getAggregatedStaffingClosingStatuses(closingStatuses)
			.map((staffedUser) => ({
				...staffedUser,
				fullName: users.find((u) => u.id === staffedUser.userId)?.fullName ?? "",
			}))
			.sort(byObjectProperty("fullName"));
	}, [users, closingStatuses]);

	return (
		<ul
			className="p-1"
			style={{ columnCount, columnGap: "0.75rem" }}
		>
			{enrichedUsers.map((user) => {
				const monthNames = user.unclosedMonths.map(formatMonthLabel).join(", ");
				return (
					<li key={user.userId}>
						<NavLink to={generatePath(TIME_TRACKINGS_PATH, { userId: user.userId })}
								 className="group flex break-inside-avoid flex-wrap items-center gap-1 px-1 py-0.5 transition-colors">
							{user.hasClosed
								?
								<CheckCircleIcon className="size-4 shrink-0 text-success-500 group-hover:text-success-700" />
								:
								<XCircleIcon className="size-4 shrink-0 text-danger-500 group-hover:text-danger-700" />
							}
							<span className="relative shrink-0 text-xs">
								<span className="invisible font-bold">{user.fullName}</span>
								<span className="absolute inset-0 font-medium group-hover:font-bold">{user.fullName}</span>
							</span>
							{showUnclosedMonths && monthNames &&
								<span className="relative text-xs text-danger-700 group-hover:font-bold">
									<span className="invisible font-bold">{monthNames}</span>
									<span className="absolute inset-0 font-medium group-hover:font-bold">{monthNames}</span>
								</span>
							}
						</NavLink>
					</li>
				);
			})}
		</ul>
	);
}
